/**
 * Analytics utility for tracking conversion events
 * 
 * Tracks key user actions in the conversion funnel:
 * - Page views
 * - Checkout initiated
 * - Subscription completed
 * - Onboarding completion
 */

export interface AnalyticsEvent {
  event: string;
  properties?: Record<string, any>;
  timestamp: Date;
  userId?: string;
}

class Analytics {
  private queue: AnalyticsEvent[] = [];
  private apiUrl: string;

  constructor() {
    this.apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
  }

  /**
   * Track an analytics event
   */
  track(event: string, properties?: Record<string, any>): void {
    const userId = this.getUserId();
    const eventData: AnalyticsEvent = {
      event,
      properties: {
        ...properties,
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer,
      },
      timestamp: new Date(),
      userId,
    };

    // Log to console in development
    if (import.meta.env.DEV) {
      console.log('[Analytics]', event, eventData);
    }

    // Add to queue
    this.queue.push(eventData);

    // Send to backend (non-blocking)
    this.sendEvent(eventData).catch((error) => {
      console.error('[Analytics] Failed to send event:', error);
    });
  }

  /**
   * Send event to backend
   */
  private async sendEvent(eventData: AnalyticsEvent): Promise<void> {
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`${this.apiUrl}/api/analytics/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(eventData),
      });
    } catch (error) {
      // Silently fail - analytics should not break the app
      console.warn('[Analytics] Could not send event to backend:', error);
    }
  }

  /**
   * Get current user ID from localStorage or context
   */
  private getUserId(): string | undefined {
    try {
      // Try to get from auth context if available
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.id;
      }
    } catch {
      // Ignore parsing errors
    }
    return undefined;
  }

  /**
   * Track page view
   */
  pageView(pageName: string, properties?: Record<string, any>): void {
    this.track('page_view', {
      page: pageName,
      ...properties,
    });
  }

  /**
   * Track checkout initiated
   */
  checkoutInitiated(priceId: string, planType: string): void {
    this.track('checkout_initiated', {
      price_id: priceId,
      plan_type: planType,
    });
  }

  /**
   * Track subscription completed
   */
  subscriptionCompleted(subscriptionId: string, planType: string): void {
    this.track('subscription_completed', {
      subscription_id: subscriptionId,
      plan_type: planType,
    });
  }

  /**
   * Track onboarding step completion
   */
  onboardingStepCompleted(step: number, stepName: string): void {
    this.track('onboarding_step_completed', {
      step,
      step_name: stepName,
    });
  }

  /**
   * Track onboarding completion
   */
  onboardingCompleted(stepsCompleted: number): void {
    this.track('onboarding_completed', {
      steps_completed: stepsCompleted,
    });
  }

  /**
   * Track value dashboard view
   */
  valueDashboardViewed(isSubscribed: boolean): void {
    this.track('value_dashboard_viewed', {
      is_subscribed: isSubscribed,
    });
  }

  /**
   * Track expedient page view
   */
  expedientPageViewed(hasLocation: boolean): void {
    this.track('expedient_page_viewed', {
      has_location: hasLocation,
    });
  }

  /**
   * Track subscription button click
   */
  subscribeButtonClicked(source: string, planType?: string): void {
    this.track('subscribe_button_clicked', {
      source,
      plan_type: planType,
    });
  }

  /**
   * Track skip action
   */
  skipAction(action: string, step?: number): void {
    this.track('skip_action', {
      action,
      step,
    });
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Export convenience functions
export const trackPageView = (pageName: string, properties?: Record<string, any>) =>
  analytics.pageView(pageName, properties);

export const trackCheckoutInitiated = (priceId: string, planType: string) =>
  analytics.checkoutInitiated(priceId, planType);

export const trackSubscriptionCompleted = (subscriptionId: string, planType: string) =>
  analytics.subscriptionCompleted(subscriptionId, planType);

export const trackOnboardingStepCompleted = (step: number, stepName: string) =>
  analytics.onboardingStepCompleted(step, stepName);

export const trackOnboardingCompleted = (stepsCompleted: number) =>
  analytics.onboardingCompleted(stepsCompleted);

export const trackValueDashboardViewed = (isSubscribed: boolean) =>
  analytics.valueDashboardViewed(isSubscribed);

export const trackExpedientPageViewed = (hasLocation: boolean) =>
  analytics.expedientPageViewed(hasLocation);

export const trackSubscribeButtonClicked = (source: string, planType?: string) =>
  analytics.subscribeButtonClicked(source, planType);

export const trackSkipAction = (action: string, step?: number) =>
  analytics.skipAction(action, step);


